import { cn } from '@/lib/utils';
import { Check, Sparkles } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';

interface DealType {
  id: string;
  name: string;
  description?: string;
  icon?: string;
}

interface DealTypeSelectorProps {
  dealTypes: DealType[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  suggestedIds?: string[];
  isLoading?: boolean;
}

function getIcon(
  iconName?: string
): ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>> {
  if (!iconName) return LucideIcons.Briefcase;
  const icons = LucideIcons as Record<string, unknown>;
  const icon = icons[iconName];
  if (typeof icon === 'object' && icon !== null && '$$typeof' in icon) {
    return icon as ForwardRefExoticComponent<
      Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>
    >;
  }
  return LucideIcons.Briefcase;
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800 p-5 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-lg bg-slate-700 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-24 rounded bg-slate-700" />
          <div className="h-3 w-full rounded bg-slate-700/60" />
          <div className="h-3 w-2/3 rounded bg-slate-700/60" />
        </div>
      </div>
    </div>
  );
}

export function DealTypeSelector({
  dealTypes,
  selectedIds,
  onToggle,
  suggestedIds = [],
  isLoading = false,
}: DealTypeSelectorProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {suggestedIds.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
          <Sparkles className="h-3.5 w-3.5 text-amber-400" />
          <span>AI suggested project types based on your documents</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {dealTypes.map((dealType) => {
          const isSelected = selectedIds.includes(dealType.id);
          const isSuggested = suggestedIds.includes(dealType.id);
          const IconComponent = getIcon(dealType.icon);

          return (
            <button
              key={dealType.id}
              type="button"
              onClick={() => onToggle(dealType.id)}
              className={cn(
                'relative rounded-xl border p-5 text-left transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-accent-500/40',
                isSelected
                  ? 'border-accent-500 bg-accent-500/10 ring-1 ring-accent-500/30'
                  : 'border-slate-700 bg-slate-800 hover:border-slate-500 hover:bg-slate-800/80'
              )}
            >
              {/* Selected checkmark */}
              {isSelected && (
                <div className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500">
                  <Check className="h-3 w-3 text-white" />
                </div>
              )}

              {/* AI suggested badge */}
              {isSuggested && !isSelected && (
                <div className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium text-amber-400">
                  <Sparkles className="h-2.5 w-2.5" />
                  Suggested
                </div>
              )}

              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                    isSelected
                      ? 'bg-accent-500/20 text-accent-400'
                      : isSuggested
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-slate-700 text-slate-400'
                  )}
                >
                  <IconComponent className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 pr-6">
                  <p className="text-sm font-semibold text-white">
                    {dealType.name}
                  </p>
                  {dealType.description && (
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      {dealType.description}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-slate-500 mt-2">
        Select one or more project types that apply to your needs
      </p>
    </div>
  );
}
